import React from "react";
import { Icon, EmptyState, notify, confirmDialog, useLocalPref, useNarrowScreen } from "./registry";
import { LeTabs, LeSection, LeModal, LeScore, OutcomeButtons, leTimeAgo, leErrText, leContactName } from "./leadengine";
/* ============================================================
   Fence Works — FW Admin → Lead Engine → Outreach (platform owner only)
   docs/LEAD_ENGINE_PLAN.md §7 (the Website pitch), §8 (CAN-SPAM, TCPA),
   §13 (the owner approves the first 50 emails), §15 (turning it on).

   Outreach tab     Queue (the emails waiting for you, rendered as the
                    prospect sees them: Approve & send / Edit / Skip),
                    Sequence (each step's day and words, with merge-field
                    chips and a live preview with a real prospect), Sent,
                    Do not contact, Setup (the checklist, the warm-up, the
                    approvals so far).
   OutreachPanel    the prospect page's card: Start pitch / Pause / Resume /
                    Stop / Mark replied, and each step's state.
   Today            call tasks (script, talking points, voicemail) and replies
                    waiting — leadengine.tsx's Today page uses
                    useOutreachToday + LoCallCard + LoReplyCard.

   Everything goes through window.VFP_DB.leadengine.call → the service's
   /leadengine/outreach routes (server/src/leadengine/outreachroutes.js).
   An email is shown in a sandboxed frame: no scripts, links open a new tab.
   ============================================================ */

/* ---------------- pure helpers (scripts/unit-outreach.mjs tests these) ---------------- */
const LO_STATES: Record<string, [string, string]> = {
  awaiting_approval: ["Waiting for you", "var(--amber)"],
  queued: ["Approved · goes out next", "var(--violet)"],
  sending: ["Sending", "var(--violet)"],
  sent: ["Sent", "var(--teal)"],
  failed: ["Didn't send", "var(--red)"],
  bounced: ["Bounced", "var(--red)"],
  complained: ["Marked as spam", "var(--red)"],
  skipped: ["Skipped", "var(--fg-3)"],
  call_due: ["Call due", "var(--amber)"],
  call_done: ["Called", "var(--green)"],
  upcoming: ["Upcoming", "var(--fg-3)"],
  not_started: ["Not started", "var(--fg-3)"],
  off: ["Turned off", "var(--fg-3)"],
};
const LO_ENROLL: Record<string, [string, string]> = { active: ["Running", "var(--green)"], paused: ["Paused", "var(--amber)"], stopped: ["Stopped", "var(--fg-3)"], completed: ["Finished", "var(--teal)"] };
const LO_SKIP: Record<string, string> = {
  no_email: "no email", no_site: "no preview site", expiring: "a preview about to expire", suppressed: "on the do-not-contact list", do_not_contact: "marked do-not-contact",
  signed_up: "already signed up", closed: "lost or not a fit", claimed: "already claimed their site", active: "already running", completed: "already finished",
  stopped: "stopped before (a reply, bounce or unsubscribe)", dismissed: "deleted before", not_found: "not found",
};
const LO_REASONS: [string, string][] = [["asked", "They asked"], ["unsubscribed", "Unsubscribed"], ["bounced", "Bounced"], ["complained", "Marked as spam"], ["customer", "Already a customer"]];
const LO_SENT_FILTERS: [string, string][] = [["", "All"], ["opened", "Opened"], ["clicked", "Clicked"], ["replied", "Replied"], ["bounced", "Bounced"], ["complained", "Spam"], ["unsubscribed", "Unsubscribed"], ["failed", "Didn't send"]];

function loState(s: any): { label: string; color: string } {
  const x = LO_STATES[String(s || "")];
  return x ? { label: x[0], color: x[1] } : { label: String(s || "").replace(/_/g, " "), color: "var(--fg-3)" };
}
/* a sent email's one word, the most telling first */
function loSentLabel(m: any): { label: string; color: string } {
  if (!m) return { label: "", color: "var(--fg-3)" };
  if (m.replied_at) return { label: "Replied", color: "var(--green)" };
  if (m.unsubscribed_at) return { label: "Unsubscribed", color: "var(--red)" };
  if (m.status === "complained") return { label: "Marked as spam", color: "var(--red)" };
  if (m.status === "bounced") return { label: "Bounced", color: "var(--red)" };
  if (m.status === "failed") return { label: "Didn't send", color: "var(--red)" };
  if (m.clicked_at) return { label: "Clicked", color: "var(--teal)" };
  if (m.opened_at) return { label: "Opened", color: "var(--teal)" };
  return { label: "Sent", color: "var(--fg-2)" };
}
/* a merge field dropped in at the cursor → the new text and where the cursor goes */
function loInsert(value: any, start: any, end: any, snippet: string): { value: string; cursor: number } {
  const v = String(value == null ? "" : value);
  const at = (x: any, dflt: number) => (x == null || x === "" || !Number.isFinite(Number(x)) ? dflt : Number(x));
  const a = Math.max(0, Math.min(v.length, at(start, v.length)));
  const b = Math.max(a, Math.min(v.length, at(end, a)));
  return { value: v.slice(0, a) + snippet + v.slice(b), cursor: a + snippet.length };
}
/* where the approval gate stands, in a sentence */
function loApprovalText(a: any): string {
  if (!a) return "";
  if (a.approveEvery) return "Approve every email is on: nothing goes out until you approve it.";
  if (a.approved < a.required) return a.approved + " of the first " + a.required + " approved. Until " + a.required + ", every email waits for you here.";
  return "Past the first " + a.required + ": emails go out on their own. Turn on Approve every email to keep checking each one.";
}
/* the bulk "Start pitch" answer → one line */
function loPitchSummary(r: any): string {
  if (!r) return "";
  const started = Number(r.done != null ? r.done : (r.started || 0) + (r.resumed || 0)) || 0;
  const parts = Object.keys(r.skipped || {}).filter((k) => r.skipped[k]).map((k) => r.skipped[k] + " " + (LO_SKIP[k] || k.replace(/_/g, " ")));
  const head = started ? "Started " + started + " pitch" + (started === 1 ? "" : "es") + "." : "No pitches started.";
  return parts.length ? head + " Skipped: " + parts.join(", ") + "." : head;
}
/* "4 of 7 done" */
function loSetupCount(items: any[]): { done: number; total: number } {
  const list = Array.isArray(items) ? items : [];
  return { done: list.filter((i) => i && i.ok).length, total: list.length };
}
/* a step's "when" on the prospect card */
function loStepWhen(step: any): string {
  if (!step || !step.at) return "";
  const t = Date.parse(step.at);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (step.state === "upcoming") return "due " + d;
  if (step.state === "sent" || step.state === "bounced" || step.state === "complained") return "sent " + d;
  if (step.state === "call_due") return "due " + d;
  if (step.state === "call_done") return "called " + d;
  return d;
}
/* the merge fields a step can use (a call has no link or picture) */
function loVarsFor(vars: any[], channel: string): any[] {
  return (vars || []).filter((v) => !v.channels || v.channels.includes(channel));
}
/* the sending state in a line */
function loSendingLine(s: any): string {
  if (!s) return "";
  if (!s.enabled) return "The queue fills so you can read the emails first; nothing goes out.";
  if (!s.ready) return "Nothing goes out until every setup item is done.";
  if (s.limit === 0) return "Starts " + (s.startDate || "on the start date") + ".";
  return s.sentToday + " of " + s.limit + " sent today · " + (s.stage || "") + (s.day ? " (day " + s.day + ")" : "");
}

/* ---------------- plumbing ---------------- */
const loApi = (): any => (window.VFP_DB && (window.VFP_DB as any).leadengine) || null;
const loCall = (path: string, body?: any) => loApi().call("/outreach" + path, body);
const isMigration27 = (e: any) => !!e && e.status === 503 && e.code === "migration" && !!(e.detail && e.detail.migration && e.detail.migration.number === 27);
const when = (v: any) => { const t = v ? Date.parse(v) : NaN; return Number.isFinite(t) ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"; };

function useLoLoad(fn: (call: typeof loCall) => Promise<any>, deps: any[] = []) {
  const api = loApi();
  const live = !!(api && api.enabled && api.enabled());
  const [data, setData] = React.useState<any>(null);
  const [denied, setDenied] = React.useState<any>(false);
  const [migration, setMigration] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const seq = React.useRef(0);
  const load = React.useCallback(() => {
    if (!live) return Promise.resolve();
    const my = ++seq.current;
    return fn(loCall).then((d: any) => { if (my === seq.current) { setData(d); setErr(""); setMigration(null); } })
      .catch((e: any) => {
        if (my !== seq.current) return;
        if (e && e.status === 403) setDenied(true);
        else if (isMigration27(e)) setMigration(e.detail.migration);
        else if (e && e.status === 503 && e.code === "migration") setMigration({ number: 25, file: "supabase/migrate_2026-10-03_lead_engine.sql", message: e.message });
        else setErr(leErrText(e));
      });
  }, [live, ...deps]);
  React.useEffect(() => { load(); }, [load]);
  return { live, data, setData, denied, migration, err, load };
}

function LoMigration({ info }: any) {
  return (
    <div className="card pad le-setup">
      <div className="kicker" style={{ color: "var(--amber)" }}>One step before outreach works</div>
      <h2 className="display" style={{ fontSize: 21, margin: "6px 0 8px" }}>Run migration #{info.number} in Supabase</h2>
      <p className="muted" style={{ margin: "0 0 10px", fontSize: 14 }}>{info.message}</p>
      <ol style={{ margin: "0 0 12px", paddingLeft: 20, fontSize: 14, lineHeight: 1.7 }}>
        <li>Open Supabase → <b>SQL editor</b>.</li>
        <li>Paste <code>{info.file}</code> and press <b>Run</b>. It's safe to run twice, and it sends nothing on its own.</li>
        <li>Come back and refresh this page.</li>
      </ol>
      <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}><Icon name="undo" size={13} />Refresh</button>
    </div>
  );
}

/* an email as the prospect will see it. The HTML is the service's own render
   (scraped values escaped there); the frame runs no scripts and opens links
   in a new tab. */
function LoEmailFrame({ html, minHeight = 240 }: any) {
  const ref = React.useRef<any>(null);
  const [h, setH] = React.useState<number>(minHeight);
  const doc = '<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:14px 16px;background:#fff}</style></head><body>' + String(html || "") + "</body></html>";
  const fit = () => { try { const b = ref.current && ref.current.contentDocument && ref.current.contentDocument.body; if (b) setH(Math.max(minHeight, Math.min(1400, b.scrollHeight + 32))); } catch (e) { /* sandboxed */ } };
  return <iframe ref={ref} title="Email as they'll see it" className="lo-frame" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} onLoad={() => { fit(); setTimeout(fit, 400); }} style={{ height: h }} />;
}

function LoChip({ state }: any) {
  const s = loState(state);
  return <span className="chip" style={{ fontSize: 11, padding: "1px 8px", color: s.color, borderColor: "currentColor", background: "transparent", whiteSpace: "nowrap" }}>{s.label}</span>;
}

/* subject + body with merge-field chips that drop in at the cursor */
function LoWords({ subject, body, voicemail, channel, vars, onChange, idp = "lo" }: any) {
  const bodyRef = React.useRef<any>(null);
  const subjRef = React.useRef<any>(null);
  const vmRef = React.useRef<any>(null);
  const last = React.useRef<string>("body");
  function drop(name: string) {
    const field = last.current;
    const el = field === "subject" ? subjRef.current : field === "voicemail" ? vmRef.current : bodyRef.current;
    const cur = field === "subject" ? subject : field === "voicemail" ? voicemail : body;
    const snip = name === "image" ? "\n\n{image}\n\n" : "{" + name + "}";
    const r = loInsert(cur, el ? el.selectionStart : null, el ? el.selectionEnd : null, snip);
    onChange({ [field]: r.value });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.cursor, r.cursor); } });
  }
  const list = loVarsFor(vars, channel);
  return (
    <div>
      {channel === "email" && <>
        <label className="lo-lab" htmlFor={idp + "-subject"}>Subject</label>
        <input id={idp + "-subject"} ref={subjRef} className="input" value={subject || ""} onFocus={() => { last.current = "subject"; }} onChange={(e) => onChange({ subject: e.target.value })} maxLength={150} />
      </>}
      <label className="lo-lab" htmlFor={idp + "-body"}>{channel === "call" ? "Call script" : "Email"}</label>
      <div className="lo-chips" role="group" aria-label="Insert a field">
        {list.map((v: any) => <button key={v.name} type="button" className="chip lo-var" title={v.help + (v.example ? " — e.g. " + v.example : "")} onClick={() => drop(v.name)}>{v.label}</button>)}
      </div>
      <textarea id={idp + "-body"} ref={bodyRef} className="textarea mono" rows={channel === "call" ? 6 : 14} value={body || ""} onFocus={() => { last.current = "body"; }} onChange={(e) => onChange({ body: e.target.value })} style={{ width: "100%", fontSize: 12.5, lineHeight: 1.55 }} />
      {channel === "email" && <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>Plain words. A blank line starts a new paragraph. <b>{"{link}"}</b> is the one link; <b>{"{image}"}</b> on its own line is the side-by-side picture.</div>}
      {channel === "call" && <>
        <label className="lo-lab" htmlFor={idp + "-vm"}>Voicemail script</label>
        <textarea id={idp + "-vm"} ref={vmRef} className="textarea" rows={3} value={voicemail || ""} onFocus={() => { last.current = "voicemail"; }} onChange={(e) => onChange({ voicemail: e.target.value })} style={{ width: "100%", fontSize: 13 }} />
        <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>Talking points come from their website audit on their own — you don't write those.</div>
      </>}
    </div>
  );
}

/* ============================================================
   The Outreach tab
   ============================================================ */
const LO_TABS: [string, string][] = [["queue", "Queue"], ["sequence", "Sequence"], ["sent", "Sent"], ["suppressions", "Do not contact"], ["setup", "Setup"]];

function OutreachPage({ go, params }: any) {
  const [pref, setPref] = useLocalPref("lo_tab", "queue");
  const tab = (params && params.tab && LO_TABS.some((t) => t[0] === params.tab)) ? params.tab : pref;
  const setTab = (t: string) => { setPref(t); if (params && params.tab) go("leadengine-outreach"); };
  const { live, data: ov, denied, migration, err, load } = useLoLoad((c) => c(""), []);
  if (!live) return <div className="page fade-up" style={{ maxWidth: 640 }}><div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="target" title="Needs the live service" sub="Outreach runs on the automation service (app.fenceworks.ai)." /></div></div>;
  if (denied) return <div className="page fade-up" style={{ maxWidth: 640 }}><div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Owner only" sub="Limited to the platform owner's account." /></div></div>;
  const s = ov && ov.sending;
  const setupN = ov ? loSetupCount(ov.setup.items) : null;
  const counts = (ov && ov.counts) || {};
  return (
    <div className="page fade-up le-page" style={{ maxWidth: 1300 }}>
      <div className="le-head" style={{ marginBottom: 10 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Outreach</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>The Website pitch: an email with the site you built them, a call on day 2, two more emails. You read the first 50 before they go.</p>
      </div>
      <LeTabs route="leadengine-outreach" go={go} />
      {migration ? <LoMigration info={migration} /> : <>
        {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
        {ov && (
          <div className={"lo-status " + (s.on ? "on" : "off")} role="status">
            <span className={"lo-dot" + (s.on ? " on" : "")} aria-hidden />
            <div style={{ minWidth: 0, flex: 1 }}>
              <b>{s.on ? "Sending" : s.enabled ? "Setup unfinished" : "Sending is off"}</b>
              <span className="muted"> · {loSendingLine(s)}</span>
              <div className="faint" style={{ fontSize: 12 }}>{loApprovalText(ov.approvals)}</div>
            </div>
            <div className="lo-status-nums">
              <div><b className="tnum">{counts.awaiting || 0}</b><span>waiting for you</span></div>
              <div><b className="tnum">{counts.active || 0}</b><span>pitches running</span></div>
              <div><b className="tnum">{counts.sentWeek || 0}</b><span>sent this week</span></div>
              <div><b className="tnum">{counts.repliedWeek || 0}</b><span>replied</span></div>
            </div>
          </div>
        )}
        <div className="lo-subtabs" role="tablist" aria-label="Outreach">
          {LO_TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={"chip" + (tab === k ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setTab(k)}>
              {l}{k === "queue" && counts.awaiting ? <span className="le-count" style={{ background: "var(--amber)" }}>{counts.awaiting}</span> : null}
              {k === "setup" && setupN && setupN.done < setupN.total ? <span className="faint" style={{ marginLeft: 6, fontSize: 11 }}>{setupN.done}/{setupN.total}</span> : null}
            </button>
          ))}
        </div>
        {tab === "queue" && <LoQueue go={go} ov={ov} onChange={load} />}
        {tab === "sequence" && <LoSequence />}
        {tab === "sent" && <LoSent go={go} />}
        {tab === "suppressions" && <LoSuppressions />}
        {tab === "setup" && <LoSetup ov={ov} onChange={load} />}
      </>}
    </div>
  );
}

/* ---------------- Setup ---------------- */
function LoSetup({ ov, onChange }: any) {
  const [busy, setBusy] = React.useState<any>(null);
  if (!ov) return <p className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</p>;
  const st = ov.setup, s = ov.sending, a = ov.approvals;
  const n = loSetupCount(st.items);
  async function toggleEvery(v: boolean) {
    setBusy("every");
    try { await loCall("/settings", { approve_every: v }); notify(v ? "Every email now waits for you." : "After the first 50, emails go on their own.", { kind: "success" }); onChange(); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  async function test() {
    setBusy("test");
    try { const r = await loCall("/test", { step_key: "email1" }); notify("Sent email 1 to " + r.to + " — check your inbox (and the spam folder).", { kind: "success" }); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  async function tick() {
    setBusy("tick");
    try { const r = await loCall("/tick", {}); const x = r.result || {}; notify("Checked: " + [x.planned ? x.planned + " new in the queue" : "", x.calls ? x.calls + " call task" + (x.calls === 1 ? "" : "s") : "", x.sent ? x.sent + " sent" : "", x.stopped ? x.stopped + " stopped" : ""].filter(Boolean).join(", ") + (x.planned || x.calls || x.sent || x.stopped ? "." : "nothing new.")); onChange(); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  const copy = (t: string) => { try { navigator.clipboard.writeText(t).then(() => notify("Copied.", { kind: "success" })); } catch (e) { window.prompt("Copy", t); } };
  return (
    <div className="lo-grid">
      <LeSection title={"Setup · " + n.done + " of " + n.total + " done"} right={st.ready ? <span className="chip green" style={{ fontSize: 11 }}>Ready</span> : <span className="chip amber" style={{ fontSize: 11 }}>Not ready</span>}>
        <div className="lo-checklist">
          {st.items.map((i: any) => (
            <div key={i.key} className="le-check">
              <span className={"le-tick " + (i.ok ? "ok" : "bad")} aria-label={i.ok ? "done" : "not done"}><Icon name={i.ok ? "check" : "x"} size={11} /></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{i.label} <code className="faint" style={{ fontSize: 11, fontWeight: 400 }}>{i.env}</code></div>
                {i.detail && <div className="faint" style={{ fontSize: 12, overflowWrap: "anywhere" }}>{i.detail}</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="kicker" style={{ margin: "14px 0 6px" }}>Webhook addresses</div>
        <div className="lo-hooks">
          <div><span className="muted">Resend (bounces, complaints)</span><code>{st.webhooks.resend}</code><button className="btn btn-ghost btn-sm" onClick={() => copy(st.webhooks.resend)}>Copy</button></div>
          <div><span className="muted">Inbound replies</span><code>{st.webhooks.inbound}</code><button className="btn btn-ghost btn-sm" onClick={() => copy(st.webhooks.inbound)}>Copy</button></div>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "12px 0 0" }}>The steps, in order, are in docs/LEAD_ENGINE_PLAN.md §15 "Turning on outreach". Env vars go in the server's .env; restart with <code>pm2 restart &lt;app&gt; --update-env</code>.</p>
      </LeSection>
      <div className="le-col">
        <LeSection title="Sending">
          <div className="le-kv">
            <span className="muted">Switch</span><span>{s.enabled ? "OUTREACH=on" : "Off"}</span>
            <span className="muted">From</span><span style={{ overflowWrap: "anywhere" }}>{s.from || "—"}{s.from && !s.fromOk ? <b style={{ color: "var(--red)" }}> · refused: product domain</b> : null}</span>
            <span className="muted">Today</span><span className="tnum">{s.sentToday} of {s.limit} sent</span>
            <span className="muted">Warm-up</span><span>{s.day ? "Day " + s.day + " · " + s.stage : s.stage || "—"}{s.startDate ? " (from " + s.startDate + ")" : ""}</span>
            <span className="muted">Cap</span><span>{s.cap} a day once warmed up</span>
            <span className="muted">Hours</span><span>Weekdays {s.hours.from}:00–{s.hours.to}:00 in the prospect's time zone</span>
            <span className="muted">Last send</span><span>{s.lastSentAt ? leTimeAgo(s.lastSentAt) : "never"}</span>
          </div>
          <div className="row gap-6" style={{ marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={test}><Icon name="send" size={12} />{busy === "test" ? "Sending…" : "Send me a test"}</button>
            <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={tick}><Icon name="undo" size={12} />{busy === "tick" ? "Checking…" : "Check now"}</button>
          </div>
        </LeSection>
        <LeSection title="Approvals">
          <div className="lo-meter" aria-label={a.approved + " of " + a.required + " approved"}><span style={{ width: Math.min(100, (a.approved / a.required) * 100) + "%" }} /></div>
          <p style={{ fontSize: 13, margin: "8px 0 10px" }}>{loApprovalText(a)}</p>
          <label className="row gap-6" style={{ fontSize: 13, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={!!a.approveEvery} disabled={busy === "every"} onChange={(e) => toggleEvery(e.target.checked)} /> Approve every email
          </label>
        </LeSection>
        <LeSection title="This week">
          <div className="lo-week">
            {[["Sent", ov.counts.sentWeek], ["Opened", ov.counts.openedWeek], ["Clicked", ov.counts.clickedWeek], ["Replied", ov.counts.repliedWeek], ["Bounced", ov.counts.bouncedWeek], ["Spam", ov.counts.complainedWeek]].map(([k, v]: any) => <div key={k}><b className="tnum">{v || 0}</b><span>{k}</span></div>)}
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Opens are for information only — Apple Mail opens every email by itself. Keep bounces under 3% and spam complaints under 0.1%.</p>
        </LeSection>
      </div>
    </div>
  );
}

/* ---------------- Queue ---------------- */
function LoQueue({ go, ov, onChange }: any) {
  const { data, load, err } = useLoLoad((c) => c("/queue?limit=40"), []);
  const [busy, setBusy] = React.useState<any>(false);
  const reload = () => { load(); onChange && onChange(); };
  if (err) return <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>;
  if (!data) return <p className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</p>;
  const list: any[] = data.messages || [];
  const waiting = list.filter((m) => m.status === "awaiting_approval");
  async function approveAll() {
    const ok = await confirmDialog({ title: "Approve all " + waiting.length + "?", body: "They go out one at a time, a few minutes apart, in each prospect's business hours — within today's limit. You can still skip one from the queue until it goes.", confirmLabel: "Approve " + waiting.length });
    if (!ok) return;
    setBusy(true);
    try { const r = await loCall("/approve", { ids: waiting.map((m) => m.id) }); notify("Approved " + r.approved + ".", { kind: "success" }); reload(); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(false); }
  }
  if (!list.length) {
    return (
      <div className="card pad">
        <EmptyState icon="mail" title="Nothing waiting" sub="Start a pitch from a prospect's page, or tick prospects in the Pipeline and press Start pitch. Each first email lands here for you to read before it goes."
          action={{ label: "Open the pipeline", icon: "target", onClick: () => go("leadengine") }} />
      </div>
    );
  }
  return (
    <div>
      <div className="row between" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <span className="muted" style={{ fontSize: 13 }}>{data.counts.awaiting} waiting for you · {data.counts.queued} approved and queued{data.counts.failed ? " · " + data.counts.failed + " didn't send" : ""}{data.total > list.length ? " · showing " + list.length + " of " + data.total : ""}</span>
        {waiting.length > 1 && <button className="btn btn-primary btn-sm" disabled={busy} onClick={approveAll}><Icon name="check" size={13} />Approve all {waiting.length} shown</button>}
      </div>
      <div className="lo-queue">
        {list.map((m) => <LoQueueCard key={m.id} m={m} go={go} onDone={reload} sendingOn={!!(ov && ov.sending && ov.sending.on)} />)}
      </div>
    </div>
  );
}

function LoQueueCard({ m, go, onDone, sendingOn }: any) {
  const [edit, setEdit] = React.useState<any>(null);
  const [vars, setVars] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<any>(null);
  const p = m.prospect || {};
  async function openEdit() {
    setEdit({ subject: m.words.subject, body: m.words.body });
    if (!vars) loCall("/sequence").then((d: any) => setVars(d.mergeVars || [])).catch(() => setVars([]));
  }
  async function act(kind: string) {
    setBusy(kind);
    try {
      if (kind === "approve") {
        const r = await loCall("/messages/" + m.id + "/approve", edit ? { subject: edit.subject, body: edit.body } : {});
        notify(r.sent ? "Sent to " + (p.business_name || "them") + "." : "Approved. " + (r.why || ""), { kind: r.failed ? "error" : "success", ms: r.sent ? 3500 : 7000 });
      } else if (kind === "save") {
        await loCall("/messages/" + m.id + "/edit", { subject: edit.subject, body: edit.body });
        notify("Saved. It stays in the queue for you to approve.", { kind: "success" });
      } else if (kind === "skip") {
        const ok = await confirmDialog({ title: "Skip this email?", body: (p.business_name || "They") + " won't get it. The pitch moves on to the next step (the call on day 2).", confirmLabel: "Skip it" });
        if (!ok) { setBusy(null); return; }
        await loCall("/messages/" + m.id + "/skip", {});
        notify("Skipped.");
      } else if (kind === "retry") {
        await loCall("/messages/" + m.id + "/retry", {});
        notify("Back in the queue.", { kind: "success" });
      } else if (kind === "picture") {
        await loCall("/prospects/" + p.id + "/compare", {});
        notify("Picture made.", { kind: "success" });
      }
      setEdit(null);
      onDone();
    } catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  const noPic = m.words && /\{image\}/.test(m.words.body || "") && !m.image_url;
  return (
    <div className={"card lo-qcard" + (m.status === "failed" ? " failed" : "")}>
      <div className="lo-qhead">
        <div style={{ minWidth: 0 }}>
          <button className="le-name-btn small" onClick={() => go("prospect", { id: p.id })}>{p.business_name || "Prospect"}</button>
          <div className="faint" style={{ fontSize: 12 }}>{[m.step_name, [p.city, p.state].filter(Boolean).join(", "), m.when && m.when.local ? "their time " + m.when.local : ""].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="row gap-6" style={{ alignItems: "center" }}><LoChip state={m.status} />{p.score != null && <LeScore score={p.score} />}</div>
      </div>
      <div className="lo-meta">
        <span className="muted">From</span><span>{m.from || <i className="faint">OUTREACH_FROM isn't set</i>}</span>
        <span className="muted">To</span><span>{m.to || <b style={{ color: "var(--red)" }}>no email</b>}{leContactName(p) ? " (" + leContactName(p) + ")" : ""}</span>
        <span className="muted">Subject</span><b>{m.subject}</b>
      </div>
      {m.error && <p className="lo-err">{m.status === "failed" ? "Didn't send: " : ""}{m.error}</p>}
      {m.enrollment && m.enrollment.status === "paused" && <p className="lo-err" style={{ color: "var(--amber)" }}>The pitch is paused{m.enrollment.pause_reason === "preview_expired" ? " — the preview expired. Extend it on the prospect page, then resume." : "."}</p>}
      {noPic && <p className="lo-warn">No picture: {(m.enrollment && m.enrollment.compare_error) || "it hasn't been made yet"}. It goes without one unless you make it. <button className="le-link-btn" disabled={!!busy} onClick={() => act("picture")}>{busy === "picture" ? "Making…" : "Make the picture"}</button></p>}
      {edit ? (
        <div className="lo-edit">
          <LoWords channel="email" subject={edit.subject} body={edit.body} vars={vars || []} idp={"q" + m.id.slice(0, 8)} onChange={(x: any) => setEdit((e: any) => ({ ...e, ...x }))} />
          <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>This changes this one email only. Change the sequence to change everyone's.</p>
        </div>
      ) : m.html ? <LoEmailFrame html={m.html} /> : <p className="muted">This email can't be shown (the prospect or its step is gone). Skip it.</p>}
      <div className="lo-qactions">
        {m.status === "awaiting_approval" && <button className="btn btn-primary btn-sm" disabled={!!busy || !m.to} onClick={() => act("approve")}><Icon name="send" size={12} />{busy === "approve" ? "…" : edit ? "Save & approve" : sendingOn ? "Approve & send" : "Approve"}</button>}
        {m.status === "failed" && <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => act("retry")}><Icon name="undo" size={12} />Retry</button>}
        {edit ? <>
          {m.status !== "awaiting_approval" && <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => act("save")}>Save</button>}
          <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => setEdit(null)}>Cancel</button>
        </> : <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={openEdit}><Icon name="edit" size={12} />Edit</button>}
        <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => act("skip")}>Skip</button>
        {m.status === "queued" && <span className="faint" style={{ fontSize: 12 }}>{m.when && !m.when.inWindow && m.when.nextLocal ? "Goes out " + m.when.nextLocal + " (their time)" : "Goes out in the next send slot"}</span>}
      </div>
    </div>
  );
}

/* ---------------- Sequence editor ---------------- */
function LoSequence() {
  const { data, load, err } = useLoLoad((c) => c("/sequence"), []);
  const [key, setKey] = React.useState<any>(null);
  const [draft, setDraft] = React.useState<any>(null);
  const [prev, setPrev] = React.useState<any>(null);
  const [pid, setPid] = useLocalPref("lo_preview_prospect", "");
  const [busy, setBusy] = React.useState<any>(null);
  const steps: any[] = (data && data.steps) || [];
  const step = steps.find((s) => s.step_key === key) || steps[0] || null;
  React.useEffect(() => { if (step && (!draft || draft.step_key !== step.step_key)) setDraft({ ...step }); }, [step && step.step_key, data]);
  const dirty = !!(draft && step && ["day", "subject", "body", "voicemail", "link", "enabled", "name"].some((k) => String(draft[k] ?? "") !== String(step[k] ?? "")));
  // the live preview, debounced
  React.useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => {
      loCall("/preview", { step_key: draft.step_key, subject: draft.subject, body: draft.body, voicemail: draft.voicemail, link: draft.link, day: draft.day, prospect_id: pid || undefined })
        .then(setPrev).catch((e: any) => setPrev({ error: leErrText(e) }));
    }, 350);
    return () => clearTimeout(t);
  }, [draft && JSON.stringify(draft), pid]);
  if (err) return <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>;
  if (!data || !draft) return <p className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</p>;
  const set = (x: any) => setDraft((d: any) => ({ ...d, ...x }));
  async function save() {
    setBusy("save");
    try { await loCall("/sequence/steps/" + draft.step_key, { day: Number(draft.day), name: draft.name, subject: draft.subject, body: draft.body, voicemail: draft.voicemail, link: draft.link, enabled: draft.enabled !== false }); notify("Saved. Emails that haven't gone yet use the new words (unless you edited one by hand).", { kind: "success" }); await load(); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  async function reset() {
    if (!(await confirmDialog({ title: "Back to the default?", body: "This step's day and words go back to the original.", confirmLabel: "Reset" }))) return;
    setBusy("reset");
    try { await loCall("/sequence/steps/" + draft.step_key + "/reset", {}); notify("Back to the default."); setDraft(null); await load(); }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  const check = (prev && prev.check) || { errors: [], warnings: [] };
  return (
    <div className="lo-seq">
      <div className="lo-steps" role="list" aria-label={data.sequence.name + " steps"}>
        <div className="kicker" style={{ marginBottom: 6 }}>{data.sequence.name}</div>
        {steps.map((s) => (
          <button key={s.step_key} role="listitem" className={"lo-step" + (s.step_key === draft.step_key ? " on" : "") + (s.enabled === false ? " off" : "")} onClick={async () => { if (s.step_key === draft.step_key) return; if (dirty && !(await confirmDialog({ title: "Drop your changes?", body: "Your edits to " + draft.name + " aren't saved.", confirmLabel: "Drop them" }))) return; setKey(s.step_key); setDraft(null); }}>
            <span className="lo-day">Day {s.day}</span>
            <span style={{ minWidth: 0 }}>
              <b><Icon name={s.channel === "call" ? "phone" : s.channel === "postcard" ? "image" : "mail"} size={12} /> {s.name}</b>
              <span className="faint" style={{ display: "block", fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.channel === "email" ? s.subject : s.channel === "call" ? "Call task on Today" : "Postcard"}</span>
            </span>
            {s.edited && <span className="chip teal" style={{ fontSize: 10, padding: "0 6px" }}>edited</span>}
            {s.enabled === false && <span className="chip" style={{ fontSize: 10, padding: "0 6px" }}>off</span>}
          </button>
        ))}
        <p className="faint" style={{ fontSize: 11.5, margin: "8px 2px 0" }}>A step never comes sooner after the one before it than the days between them — if an email waits for you, the rest move back.</p>
      </div>
      <div className="card lo-editor">
        <div className="row between" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <label className="row gap-6" style={{ alignItems: "center", fontSize: 13 }}>Day <input className="input" type="number" min={0} max={90} value={draft.day} onChange={(e) => set({ day: e.target.value })} style={{ width: 72, padding: "5px 8px" }} aria-label="Day" /></label>
            {draft.channel === "email" && <label className="row gap-6" style={{ alignItems: "center", fontSize: 13 }}>Link <select className="select" value={draft.link || "preview"} onChange={(e) => set({ link: e.target.value })} style={{ width: 170, padding: "5px 30px 5px 8px" }} aria-label="Where the link goes"><option value="preview">Their preview site</option><option value="scorecard">The Fence Ops Scorecard</option></select></label>}
            <label className="row gap-6" style={{ alignItems: "center", fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={draft.enabled !== false} onChange={(e) => set({ enabled: e.target.checked })} /> On</label>
          </div>
          <div className="row gap-6">
            {step && step.edited && <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={reset}>Reset to default</button>}
            <button className="btn btn-primary btn-sm" disabled={!!busy || !dirty || check.errors.length > 0} onClick={save}>{busy === "save" ? "Saving…" : "Save"}</button>
          </div>
        </div>
        <label className="lo-lab" htmlFor="lo-step-name">Name</label>
        <input id="lo-step-name" className="input" value={draft.name || ""} onChange={(e) => set({ name: e.target.value })} maxLength={80} />
        <LoWords channel={draft.channel} subject={draft.subject} body={draft.body} voicemail={draft.voicemail} vars={data.mergeVars} idp="seq" onChange={set} />
        {(check.errors.length > 0 || check.warnings.length > 0) && (
          <div className="lo-checkbox">
            {check.errors.map((x: string, i: number) => <div key={"e" + i} style={{ color: "var(--red)" }}>• {x}</div>)}
            {check.warnings.map((x: string, i: number) => <div key={"w" + i} style={{ color: "var(--amber)" }}>• {x}</div>)}
          </div>
        )}
      </div>
      <div className="lo-preview">
        <div className="row between" style={{ gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
          <span className="kicker">Live preview</span>
          <select className="select" value={pid} onChange={(e) => setPid(e.target.value)} style={{ width: 230, padding: "5px 30px 5px 8px", fontSize: 12.5 }} aria-label="Preview with">
            <option value="">A sample fence company</option>
            {(data.samples || []).map((p: any) => <option key={p.id} value={p.id}>{p.business_name}{p.city ? " · " + p.city : ""}{p.email ? "" : " (no email)"}</option>)}
          </select>
        </div>
        {prev && prev.error && <p style={{ color: "var(--red)", fontSize: 13 }}>{prev.error}</p>}
        {prev && prev.email && <>
          <div className="card lo-prevhead">
            <div className="faint" style={{ fontSize: 11.5 }}>From {prev.email.from || "(OUTREACH_FROM)"} · to {prev.prospect.business_name}</div>
            <b>{prev.email.subject}</b>
          </div>
          {prev.email.needsImage && <p className="faint" style={{ fontSize: 11.5, margin: "0 0 6px" }}>The side-by-side picture goes where {"{image}"} is — it's made when their first email is queued.</p>}
          <LoEmailFrame html={prev.email.html} minHeight={320} />
        </>}
        {prev && prev.call && (
          <div className="card lo-callprev">
            <div className="kicker">Script</div><p>{prev.call.script}</p>
            {prev.call.talking_points.length > 0 && <><div className="kicker">Talking points</div><ul>{prev.call.talking_points.map((t: string, i: number) => <li key={i}>{t}</li>)}</ul></>}
            <div className="kicker">Voicemail</div><p>{prev.call.voicemail}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Sent ---------------- */
function LoSent({ go }: any) {
  const [f, setF] = useLocalPref("lo_sent_filter", "");
  const { data, err } = useLoLoad((c) => c("/sent?limit=300" + (f ? "&status=" + encodeURIComponent(f) : "")), [f]);
  const [open, setOpen] = React.useState<any>(null);
  const narrow = useNarrowScreen(720);
  const list: any[] = (data && data.messages) || [];
  async function show(m: any) {
    try { const r = await loCall("/sent/" + m.id); setOpen(r.message); } catch (e: any) { notify(leErrText(e), { kind: "error" }); }
  }
  return (
    <div>
      <div className="row gap-6" style={{ flexWrap: "wrap", marginBottom: 10 }}>
        {LO_SENT_FILTERS.map(([k, l]) => <button key={k} className={"chip" + (f === k ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setF(k)}>{l}</button>)}
      </div>
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
      {!data ? <p className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</p> : !list.length ? <div className="card pad"><EmptyState icon="send" title={f ? "None of those" : "Nothing sent yet"} sub={f ? "Try another filter." : "Every email that goes out is listed here with what happened to it."} /></div> : narrow ? (
        <div className="le-cards">
          {list.map((m) => { const s = loSentLabel(m); return (
            <button key={m.id} className="card lo-sentcard" onClick={() => show(m)}>
              <div className="row between" style={{ gap: 8 }}><b style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{m.prospect ? m.prospect.business_name : m.to}</b><span style={{ color: s.color, fontSize: 12, fontWeight: 700 }}>{s.label}</span></div>
              <div className="faint" style={{ fontSize: 12 }}>{m.step_name} · {when(m.sent_at || m.updated_at)}</div>
              {m.reply_snippet && <div style={{ fontSize: 12.5, marginTop: 4 }}>“{m.reply_snippet}”</div>}
            </button>
          ); })}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table className="le-table">
              <thead><tr><th>Sent</th><th>Prospect</th><th>Step</th><th>To</th><th>What happened</th><th /></tr></thead>
              <tbody>
                {list.map((m) => { const s = loSentLabel(m); return (
                  <tr key={m.id}>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>{when(m.sent_at || m.updated_at)}</td>
                    <td>{m.prospect ? <button className="le-name-btn small" onClick={() => go("prospect", { id: m.prospect.id })}>{m.prospect.business_name}</button> : <span className="faint">deleted</span>}</td>
                    <td style={{ fontSize: 12.5 }}>{m.step_name}</td>
                    <td style={{ fontSize: 12.5 }}>{m.to}</td>
                    <td style={{ fontSize: 12.5 }}><b style={{ color: s.color }}>{s.label}</b>{m.click_count > 1 ? " · " + m.click_count + " clicks" : ""}{m.reply_snippet ? <div className="faint" style={{ maxWidth: 320 }}>“{m.reply_snippet}”</div> : m.error ? <div className="faint" style={{ maxWidth: 320 }}>{m.error}</div> : null}</td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => show(m)}>View</button></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {open && (
        <LeModal title={open.subject || "Email"} sub={"To " + (open.to || "—") + (open.sent_at ? " · sent " + when(open.sent_at) : "")} onClose={() => setOpen(null)} wide>
          {open.html ? <LoEmailFrame html={open.html} minHeight={360} /> : <p className="muted">{open.error || "It never went out."}</p>}
        </LeModal>
      )}
    </div>
  );
}

/* ---------------- Do not contact ---------------- */
function LoSuppressions() {
  const [q, setQ] = React.useState<any>("");
  const [qq, setQq] = React.useState<any>("");
  React.useEffect(() => { const t = setTimeout(() => setQq(q), 250); return () => clearTimeout(t); }, [q]);
  const { data, load, err } = useLoLoad((c) => c("/suppressions" + (qq ? "?q=" + encodeURIComponent(qq) : "")), [qq]);
  const [value, setValue] = React.useState<any>("");
  const [reason, setReason] = React.useState<any>("asked");
  const [busy, setBusy] = React.useState<any>(false);
  async function add(e: any) {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try { const r = await loCall("/suppressions", { value, reason }); notify(r.already ? "Already on the list." : "Added. Nobody at that " + (r.row && r.row.domain ? "domain" : "address") + " gets another email.", { kind: "success" }); setValue(""); load(); }
    catch (x: any) { notify(leErrText(x), { kind: "error" }); } finally { setBusy(false); }
  }
  async function remove(r: any) {
    const ok = await confirmDialog({ title: "Take " + (r.email || r.domain) + " off the list?", body: r.reason === "unsubscribed" || r.reason === "complained" ? "They " + (r.reason === "unsubscribed" ? "unsubscribed" : "marked an email as spam") + ". Only take them off if they asked you to." : "They can be pitched again.", confirmLabel: "Take off", danger: r.reason === "unsubscribed" || r.reason === "complained" });
    if (!ok) return;
    try { await loCall("/suppressions/" + r.id + "/delete", {}); notify("Taken off."); load(); } catch (e: any) { notify(leErrText(e), { kind: "error" }); }
  }
  const rows: any[] = (data && data.rows) || [];
  return (
    <div className="lo-grid">
      <LeSection title="Never email">
        <form onSubmit={add} className="lo-addsupp">
          <input className="input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="mike@smithfence.com or smithfence.com" aria-label="Email or domain" />
          <select className="select" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why">{LO_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !value.trim()}>Add</button>
        </form>
        <p className="faint" style={{ fontSize: 12, margin: "8px 0 0" }}>A domain covers every address at it and the business whose website it is. Unsubscribes, bounces and spam complaints land here on their own and stay for good.</p>
      </LeSection>
      <LeSection title={"The list" + (data ? " · " + data.total : "")} right={<input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search the list" style={{ width: 180, padding: "5px 10px" }} />}>
        {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
        {!data ? <p className="muted">Loading…</p> : !rows.length ? <p className="muted" style={{ fontSize: 13 }}>{qq ? "Nothing matches." : "Nobody yet."}</p> : (
          <div className="lo-supps">
            {rows.map((r) => (
              <div key={r.id} className="lo-supp">
                <div style={{ minWidth: 0 }}>
                  <b style={{ overflowWrap: "anywhere" }}>{r.email || r.domain || r.phone_e164}</b>
                  <div className="faint" style={{ fontSize: 11.5 }}>{(LO_REASONS.find((x) => x[0] === r.reason) || [0, r.reason])[1]}{r.business_name ? " · " + r.business_name : ""} · {leTimeAgo(r.at)}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => remove(r)} aria-label={"Take " + (r.email || r.domain) + " off"}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </LeSection>
    </div>
  );
}

/* ============================================================
   The prospect page's Outreach card
   ============================================================ */
function OutreachPanel({ p, onChange }: any) {
  const { live, data, load, migration, err } = useLoLoad((c) => c("/prospects/" + encodeURIComponent(p.id)), [p.id, p.stage, p.email, p.do_not_contact]);
  const [busy, setBusy] = React.useState<any>(null);
  const [reply, setReply] = React.useState<any>(null);
  if (!live) return null;
  if (migration) return <LeSection title="Outreach" className="le-o-outreach"><p className="muted" style={{ fontSize: 13, margin: 0 }}>Run migration #{migration.number} (<code>{migration.file}</code>) to pitch from here.</p></LeSection>;
  async function act(kind: string, body?: any) {
    setBusy(kind);
    try {
      const r = await loCall("/prospects/" + encodeURIComponent(p.id) + "/" + kind, body || {});
      notify(kind === "start" ? (r.resumed ? "Pitch resumed." : "Pitch started — the first email is waiting for you in Outreach → Queue in a minute.") : kind === "pause" ? "Paused. Nothing more goes until you resume." : kind === "resume" ? "Resumed." : kind === "stop" ? "Stopped." : kind === "replied" ? "Marked replied — the pitch has stopped." : "Done.", { kind: "success" });
      setReply(null);
      await load(); onChange && onChange();
    } catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  async function stop() { if (await confirmDialog({ title: "Stop the pitch?", body: "Nothing more goes to " + p.business_name + " automatically. You can start it again later from where it stopped.", confirmLabel: "Stop" })) act("stop"); }
  const e = data && data.enrollment;
  const chip = e ? LO_ENROLL[e.status] : null;
  return (
    <LeSection title="Outreach" className="le-o-outreach" right={chip ? <span className="chip" style={{ fontSize: 11, color: chip[1], borderColor: "currentColor", background: "transparent" }}>{chip[0]}</span> : null}>
      {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "0 0 8px" }}>{err}</p>}
      {!data ? <p className="muted" style={{ fontSize: 13, margin: 0 }}>Loading…</p> : <>
        {e && e.status === "stopped" && <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>{e.stop_label || "Stopped"}.</p>}
        {e && e.status === "paused" && <p style={{ fontSize: 13, margin: "0 0 8px", color: "var(--amber)" }}>{e.pause_label || "Paused"}.</p>}
        {!e && data.blocker && <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>Can't pitch yet: {data.blocker.message}</p>}
        {!e && !data.blocker && <p className="muted" style={{ fontSize: 13, margin: "0 0 8px" }}>The Website pitch: an email with the site you built them today, a call on day 2, then two more emails. The first ones wait for your approval.</p>}
        <div className="lo-psteps">
          {data.steps.map((s: any) => (
            <div key={s.step_key} className={"lo-pstep s-" + s.state}>
              <span className="lo-day">Day {s.day}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}><Icon name={s.channel === "call" ? "phone" : "mail"} size={12} /> {s.name}</span>
                <span className="faint" style={{ display: "block", fontSize: 11.5 }}>{[loStepWhen(s), s.opened ? "opened" : "", s.clicked ? "clicked" : "", s.replied ? "replied" : "", s.error && s.state !== "skipped" ? s.error : ""].filter(Boolean).join(" · ")}</span>
              </span>
              <LoChip state={s.state} />
            </div>
          ))}
        </div>
        {e && e.compare_url && <a className="lo-thumb" href={e.compare_url} target="_blank" rel="noreferrer noopener" title="The side-by-side picture in email 1"><img src={e.compare_url} alt={"The side-by-side picture for " + p.business_name} loading="lazy" /></a>}
        {reply != null && (
          <div style={{ marginTop: 10 }}>
            <textarea className="textarea" rows={2} value={reply} onChange={(x) => setReply(x.target.value)} placeholder="What they said (optional)" style={{ width: "100%" }} aria-label="What they said" />
            <div className="row gap-6" style={{ marginTop: 6 }}><button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => act("replied", { text: reply })}>Mark replied</button><button className="btn btn-ghost btn-sm" onClick={() => setReply(null)}>Cancel</button></div>
          </div>
        )}
        <div className="row gap-6" style={{ marginTop: 12, flexWrap: "wrap" }}>
          {data.canStart && <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => act("start")}><Icon name="send" size={12} />{busy === "start" ? "Starting…" : e && e.status === "paused" ? "Resume pitch" : e ? "Start again" : "Start pitch"}</button>}
          {e && e.status === "active" && <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => act("pause")}><Icon name="clock" size={12} />Pause</button>}
          {e && ["active", "paused"].includes(e.status) && <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={stop}>Stop</button>}
          {e && e.status !== "stopped" && reply == null && <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => setReply("")}><Icon name="chat" size={12} />Mark replied</button>}
        </div>
        {!data.outreachOn && e && e.status === "active" && <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Sending is off (OUTREACH) — emails wait in the queue until it's on.</p>}
      </>}
    </LeSection>
  );
}

/* ============================================================
   Today: call tasks + replies waiting
   ============================================================ */
function useOutreachToday() {
  const api = loApi();
  const live = !!(api && api.enabled && api.enabled());
  const [data, setData] = React.useState<any>({ calls: [], replies: [], ready: false });
  const load = React.useCallback(() => {
    if (!live) return Promise.resolve();
    return loCall("/calls").then((d: any) => setData({ calls: d.calls || [], replies: d.replies || [], ready: true }))
      .catch(() => setData({ calls: [], replies: [], ready: true }));   // before #27 (or a blip) Today still works without them
  }, [live]);
  React.useEffect(() => { load(); }, [load]);
  return { ...data, load };
}

function LoCallScript({ c, open }: any) {
  const [show, setShow] = React.useState<any>(!!open);
  return (
    <div className="lo-script">
      <button className="le-link-btn" onClick={() => setShow(!show)} aria-expanded={show}>{show ? "Hide the script" : "Script and voicemail"}</button>
      {show && <>
        {c.script && <p style={{ whiteSpace: "pre-wrap" }}>{c.script}</p>}
        {c.voicemail && <><div className="kicker">Voicemail</div><p style={{ whiteSpace: "pre-wrap" }}>{c.voicemail}</p></>}
      </>}
    </div>
  );
}

function LoCallCard({ c, go, onLogged, narrow }: any) {
  const p = c.prospect || {};
  const tel = (() => { const d = String(p.phone_e164 || p.phone_display || "").replace(/[^\d+]/g, ""); return d.replace(/\D/g, "").length >= 10 ? "tel:" + d : null; })();
  return (
    <div className="card le-due lo-call">
      <div className="le-due-main">
        <LeScore score={p.score} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <button className="le-name-btn small" onClick={() => go("prospect", { id: p.id })}>{p.business_name}</button>
          <div style={{ fontSize: 12.5 }}><span style={{ color: "var(--fg-2)" }}>{c.step_name}</span><span className="faint"> · {[p.city, p.state].filter(Boolean).join(", ")}{c.local ? " · their time " + c.local : ""}{c.in_hours === false ? " (outside business hours)" : ""}</span></div>
        </div>
        {tel ? <a className="btn btn-primary btn-sm" href={tel}><Icon name="phone" size={13} />Call {p.phone_display || ""}</a> : <span className="faint" style={{ fontSize: 12 }}>no phone on file</span>}
      </div>
      {c.talking_points && c.talking_points.length > 0 && <div className="le-points"><span className="kicker">Talking points</span>{c.talking_points.map((t: string, i: number) => <div key={i}>• {t}</div>)}</div>}
      <LoCallScript c={c} open={!narrow} />
      <OutcomeButtons p={p} compact onLogged={() => onLogged && onLogged()} />
    </div>
  );
}

function LoReplyCard({ r, go, onDone }: any) {
  const p = r.prospect || {};
  // answering it is a touch: it logs an email, and the reply leaves Today
  const answered = () => { const api = loApi(); if (api && api.log) api.log(p.id, { type: "email", text: "Answered their reply" }).then(() => onDone && onDone(), () => {}); };
  return (
    <div className="card le-due lo-reply">
      <div className="le-due-main">
        <span className="lo-reply-ico" aria-hidden><Icon name="chat" size={14} /></span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <button className="le-name-btn small" onClick={() => go("prospect", { id: p.id })}>{p.business_name}</button>
          <div className="faint" style={{ fontSize: 12 }}>replied {leTimeAgo(r.replied_at)}{p.email ? " · " + p.email : ""}</div>
          {r.snippet && <div className="lo-quote">“{r.snippet}”</div>}
        </div>
        {p.email && <a className="btn btn-outline btn-sm" href={"mailto:" + encodeURIComponent(p.email).replace(/%40/g, "@")} onClick={answered}><Icon name="mail" size={13} />Reply</a>}
      </div>
    </div>
  );
}

Object.assign(window, { OutreachPage, OutreachPanel });

export {
  OutreachPage, OutreachPanel, useOutreachToday, LoCallCard, LoReplyCard, LoCallScript,
  loState, loSentLabel, loInsert, loApprovalText, loPitchSummary, loSetupCount, loStepWhen, loVarsFor, loSendingLine,
};
